import { FC, useMemo } from "react";

import { Theme, SxProps, useTheme, styled } from "@mui/material";

import Image from "@/components/Image/Image";
import { getYoutubeIdentifier } from "@/features/FormV2/helpers/getYoutubeIdentifier";
import { useMediaQuery } from "@/hooks/useMediaQuery";

const StyledIFrame = styled("iFrame")(() => ({}));

type MobileThemedFormSharedProps = {
  id?: string;
  "data-test"?: string;
  sx?: SxProps<Theme>;
};

export type MobileThemedFormBannerProps = MobileThemedFormSharedProps &
  (
    | { imageSrc: string; videoSrc: undefined; onLoadingComplete?: () => void }
    | { imageSrc: undefined; videoSrc: string }
  );

export const MobileThemedFormBanner: FC<MobileThemedFormBannerProps> = ({ imageSrc, videoSrc, ...rest }) => {
  if (videoSrc) {
    return <MobileThemedFormVideoBanner src={videoSrc} {...rest} />;
  }

  if (imageSrc) {
    return <MobileThemedFormImageBanner src={imageSrc} {...rest} />;
  }

  // Should never happen due to TypeScript's exhaustive checking
  return null;
};

type MobileThemedFormVideoBannerProps = MobileThemedFormSharedProps & {
  src: string;
};

const MobileThemedFormVideoBanner: FC<MobileThemedFormVideoBannerProps> = ({ src, sx, ...rest }) => {
  const { isSmallScreen } = useMediaQuery();
  const youtubeIdentifier = useMemo(() => getYoutubeIdentifier(src), [src]);

  if (!isSmallScreen) {
    return null;
  }

  const styles = {
    width: "100%",
    maxHeight: "300px",
    aspectRatio: "16 / 9",
    lineHeight: 0,
    ...(sx as object), // StyledIFrame does not support the full range of sx props
  };

  return (
    <StyledIFrame
      src={`https://www.youtube.com/embed/${youtubeIdentifier}?autoplay=1&mute=1&loop=1;showinfo=0`}
      allowFullScreen
      frameBorder="0"
      allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture;"
      data-chromatic="ignore"
      sx={styles}
      {...rest}
    />
  );
};

type MobileThemedFormImageBannerProps = MobileThemedFormSharedProps & {
  src: string;
  onLoadingComplete?: () => void;
};

const MobileThemedFormImageBanner: FC<MobileThemedFormImageBannerProps> = ({ src, onLoadingComplete, sx, ...rest }) => {
  const theme = useTheme();
  const { isSmallScreen } = useMediaQuery();

  if (!isSmallScreen) {
    return null;
  }

  const styles = {
    maxHeight: "300px",
    backgroundColor: theme.palette.forms.surface.quiet,
    aspectRatio: "16 / 9",
    objectFit: "cover",
    ...(sx as object), // Image.tsx does not support the full range of sx props
  };

  return (
    <Image
      src={src}
      alt=""
      resolution={1200}
      priority
      onLoadingComplete={onLoadingComplete}
      data-test="banner-image"
      width="100%"
      height="auto"
      sx={styles}
      {...rest}
    />
  );
};
